import { Skeleton } from '@/components/ui'
import { formatNumber, humanizeDate } from '@/utils'
import { useSummary } from '../accountStore'
import { useCreditBank, useLoadingCreditBank } from '../creditBankStore'
import type { Wallet } from '../types'
import { ReservedCreditsIndicator } from './ReservedCreditsIndicator'

export const CreditBalance = ({ wallet }: { wallet: Wallet }) => {
  const balance = useCreditBank(wallet)
  const isLoading = useLoadingCreditBank(wallet)
  const summary = useSummary()
  // Reservations only ever exist on the pages wallet — screenings never
  // reserves, so this is always 0 there.
  const reserved = wallet === 'pages' ? (summary?.pagesCreditsReserved ?? 0) : 0

  if (isLoading && !balance) {
    return <Skeleton className="h-32 w-full rounded-xl" />
  }

  return (
    <div className="rounded-xl border bg-white p-5 lg:max-w-2xl">
      <p className="font-semibold leading-none tracking-tight">
        Your current balance
      </p>
      <div className="grid lg:grid-cols-[19rem_minmax(0,1fr)] lg:items-center">
        <div>
          <p className="mt-1 flex items-center gap-2 text-5xl font-bold tracking-tight">
            {formatNumber(balance?.credits ?? 0)}{' '}
            <ReservedCreditsIndicator
              reserved={reserved}
              className="text-muted-foreground"
            />
          </p>
          <span className="text-muted-foreground text-lg font-medium tracking-normal">
            {wallet} credits
          </span>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 lg:mt-0 lg:border-t-0 lg:pt-0">
          <div className="flex items-start justify-between pb-4 border-b border-blue-2">
            <div>
              <p className="text-muted-foreground text-sm">Plan credits</p>
              {balance?.planCreditsNextExpiry && (
                <p className="text-muted-foreground text-xs">
                  Expires {humanizeDate(balance.planCreditsNextExpiry)}
                </p>
              )}
            </div>
            <p className="text-xl font-semibold">
              {formatNumber(balance?.planCreditsRemaining ?? 0)}
            </p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">Bundle credits</p>
            <p className="text-xl font-semibold">
              {formatNumber(balance?.bundleCreditsRemaining ?? 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
